import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { FilePicker } from "@/components/ui/FilePicker";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

import {
  FIRMWARE_LIST_KEY,
  FIRMWARE_RELEASE_NOTE_PRODUCT_LINES,
  FIRMWARE_RELEASE_NOTES_LIST_KEY,
  createFirmware,
  createFirmwareReleaseNote,
  fetchFirmwareReleaseNoteDetail,
  fetchFirmwareReleaseNotes,
  firmwareReleaseNoteDetailKey,
  firmwareReleaseNoteProductLineLabel,
  firmwareReleaseNoteSelectLabel,
  firmwareReleaseNoteToSelectOption,
  firmwareFeatureRequiredForFamily,
  formatFirmwareFixtureFileName,
  withFirmwareReleaseNoteSelectOption,
  type FirmwareReleaseNoteProductLine,
} from "./firmwareApi";
import { FirmwareAppliesFields, appendFirmwareAppliesFormData, type FirmwareAppliesFormValue } from "./FirmwareAppliesFields";

export type FirmwareComposeMode = "firmware" | "notes" | "both";

export type FirmwareComposeCreated = {
  releaseNoteId: number | null;
  firmwareId: number | null;
};

const MODE_HELP: Record<FirmwareComposeMode, string> = {
  firmware:
    "Add a hardware-specific firmware file linked to shared release notes. The hub names the hex from software version, family, and feature.",
  notes: "Add shared changelog text. Hardware-specific hex files are added separately.",
  both: "Add a firmware file on the left and shared changelog text on the right. The hub names the hex from the new notes, family, and feature.",
};

function composeTitle(mode: FirmwareComposeMode): string {
  if (mode === "firmware") return "New firmware file";
  if (mode === "notes") return "New release notes";
  return "New firmware file and release notes";
}

export function FirmwareComposeModal({
  open,
  mode,
  presetReleaseNoteId = null,
  onClose,
  onCreated,
}: {
  open: boolean;
  mode: FirmwareComposeMode;
  presetReleaseNoteId?: number | null;
  onClose: () => void;
  onCreated: (created: FirmwareComposeCreated) => void;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const showNotes = mode === "notes" || mode === "both";
  const showFirmware = mode === "firmware" || mode === "both";
  const [noteVersion, setNoteVersion] = useState("");
  const [productLine, setProductLine] = useState<FirmwareReleaseNoteProductLine>("sgs");
  const [notes, setNotes] = useState("");
  const [comments, setComments] = useState("");
  const [releasedOn, setReleasedOn] = useState("");
  const [applies, setApplies] = useState<FirmwareAppliesFormValue>({
    pcbaFamily: "",
    feature: "",
    revisionEffective: "",
    revisionEnding: "",
  });
  const [existingNoteId, setExistingNoteId] = useState("");
  const [noteSearch, setNoteSearch] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const notesQuery = useQuery({
    queryKey: [...FIRMWARE_RELEASE_NOTES_LIST_KEY, "compose-picker"],
    queryFn: () => fetchFirmwareReleaseNotes({ limit: 500 }),
    enabled: open && mode === "firmware",
  });

  const pickerNoteIdRaw = existingNoteId !== "" ? existingNoteId : String(presetReleaseNoteId ?? "");
  const linkedNoteId = Number.parseInt(pickerNoteIdRaw, 10);
  const linkedNoteQuery = useQuery({
    queryKey: firmwareReleaseNoteDetailKey(linkedNoteId),
    queryFn: () => fetchFirmwareReleaseNoteDetail(linkedNoteId),
    enabled: open && mode === "firmware" && Number.isFinite(linkedNoteId) && linkedNoteId > 0,
  });

  useEffect(() => {
    if (!open) {
      setNoteVersion("");
      setProductLine("sgs");
      setNotes("");
      setComments("");
      setReleasedOn("");
      setApplies({
        pcbaFamily: "",
        feature: "",
        revisionEffective: "",
        revisionEnding: "",
      });
      setExistingNoteId("");
      setNoteSearch("");
      setFile(null);
      setErrorMessage(null);
      return;
    }
    if (presetReleaseNoteId != null) {
      setExistingNoteId(String(presetReleaseNoteId));
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, presetReleaseNoteId]);

  const creatingNotes = showNotes && notes.trim() !== "" && noteVersion.trim() !== "";
  const featureReady =
    applies.pcbaFamily !== "" &&
    (!firmwareFeatureRequiredForFamily(applies.pcbaFamily) || applies.feature !== "");
  const linkedNote =
    (notesQuery.data?.releaseNotes ?? []).find((note) => String(note.id) === existingNoteId) ??
    (linkedNoteQuery.data?.releaseNote != null && String(linkedNoteQuery.data.releaseNote.id) === existingNoteId
      ? linkedNoteQuery.data.releaseNote
      : null);
  const noteOptions = useMemo(
    () =>
      withFirmwareReleaseNoteSelectOption(
        (notesQuery.data?.releaseNotes ?? []).map(firmwareReleaseNoteToSelectOption),
        linkedNote != null ? firmwareReleaseNoteToSelectOption(linkedNote) : null,
      ),
    [linkedNote, notesQuery.data?.releaseNotes],
  );
  const softwareVersionKey = creatingNotes ? noteVersion.trim() : (linkedNote?.versionKey ?? "");
  const predictedFileName =
    softwareVersionKey !== "" && applies.pcbaFamily !== ""
      ? formatFirmwareFixtureFileName({
          versionKey: softwareVersionKey,
          pcbaFamily: applies.pcbaFamily,
          feature: applies.feature === "" ? null : applies.feature,
        })
      : "";
  const creatingFirmware = showFirmware && featureReady && (creatingNotes || existingNoteId.trim() !== "");

  const createMutation = useMutation({
    mutationFn: async (): Promise<FirmwareComposeCreated> => {
      if (mode === "notes") {
        if (noteVersion.trim() === "" || notes.trim() === "") {
          throw new Error("Software version and notes are required.");
        }
      } else if (mode === "firmware") {
        if (!featureReady || existingNoteId.trim() === "") {
          throw new Error("Family, feature (when required), and linked release notes are required.");
        }
      } else if (!creatingNotes || !creatingFirmware) {
        throw new Error("Fill in both the firmware file and the release notes.");
      }

      let releaseNoteId: number | null = null;
      if (creatingNotes) {
        const created = await createFirmwareReleaseNote({
          versionKey: noteVersion.trim(),
          productLine,
          notes: notes.trim(),
          comments: comments.trim() || null,
          releasedOn: releasedOn || null,
        });
        releaseNoteId = created.releaseNote.id;
      } else if (showFirmware && existingNoteId.trim() !== "") {
        releaseNoteId = Number.parseInt(existingNoteId, 10);
        if (!Number.isFinite(releaseNoteId) || releaseNoteId < 1) {
          throw new Error("Linked release notes id is not valid.");
        }
      }

      let firmwareId: number | null = null;
      if (creatingFirmware) {
        if (releaseNoteId == null) {
          throw new Error("Link the file to release notes.");
        }
        const form = new FormData();
        appendFirmwareAppliesFormData(form, applies);
        form.set("changes", "");
        if (releasedOn) form.set("releasedOn", releasedOn);
        if (releaseNoteId != null) form.set("releaseNoteId", String(releaseNoteId));
        if (file) form.set("file", file);
        const created = await createFirmware(form);
        firmwareId = created.firmware.id;
      }

      return { releaseNoteId, firmwareId };
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: FIRMWARE_LIST_KEY });
      await queryClient.invalidateQueries({ queryKey: FIRMWARE_RELEASE_NOTES_LIST_KEY });
      onCreated(created);
      onClose();
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof ApiError || error instanceof Error ? error.message : "Could not create.");
    },
  });

  if (!open) return null;

  const selectClassName = cn(
    "mt-1.5 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
    "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
  );

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setErrorMessage(null);
    createMutation.mutate();
  };

  const firmwareSection = showFirmware ? (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-900">Firmware file</h3>
      {mode === "firmware" ? (
        <div>
          <Label htmlFor="compose-existing-note">Link existing release notes</Label>
          <div className="mt-1.5">
            <SearchableSelect
              id="compose-existing-note"
              value={existingNoteId}
              onValueChange={(option) => setExistingNoteId(option?.value ?? "")}
              options={noteOptions}
              searchValue={noteSearch}
              onSearchChange={setNoteSearch}
              placeholder="Select release notes"
              searchPlaceholder="Search version"
              emptyMessage="No release notes match."
              isLoading={notesQuery.isLoading && linkedNote == null}
              selectedLabelOverride={
                linkedNote != null ? firmwareReleaseNoteSelectLabel(linkedNote) : null
              }
            />
          </div>
        </div>
      ) : null}
      {mode === "firmware" ? (
        <div>
          <Label htmlFor="compose-firmware-released">Released</Label>
          <Input
            id="compose-firmware-released"
            type="date"
            className="mt-1.5"
            value={releasedOn}
            onChange={(event) => setReleasedOn(event.target.value)}
          />
        </div>
      ) : null}
      <FirmwareAppliesFields value={applies} onChange={setApplies} />
      <FilePicker
        id="compose-file"
        label="Firmware hex"
        accept=".hex,.zip,application/zip,application/octet-stream"
        hoverHint="Upload the .hex, or a zip that contains one .hex. The hub stores the hex and names it from software version, family, and feature."
        onChange={(next) => setFile(next)}
      />
      <div>
        <Label>File name</Label>
        <p className="mt-1.5 text-sm text-slate-800">
          {predictedFileName !== "" ? predictedFileName : "Set family, feature, and release notes to see the renamed file name."}
        </p>
      </div>
    </section>
  ) : null;

  const notesSection = showNotes ? (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-900">Release notes</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="compose-note-version">Software version</Label>
          <Input
            id="compose-note-version"
            className="mt-1.5"
            value={noteVersion}
            onChange={(event) => setNoteVersion(event.target.value)}
            placeholder="6.67"
          />
        </div>
        <div>
          <Label htmlFor="compose-note-line">Product line</Label>
          <select
            id="compose-note-line"
            className={selectClassName}
            value={productLine}
            onChange={(event) => setProductLine(event.target.value as FirmwareReleaseNoteProductLine)}
          >
            {FIRMWARE_RELEASE_NOTE_PRODUCT_LINES.map((line) => (
              <option key={line} value={line}>
                {firmwareReleaseNoteProductLineLabel(line)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="compose-released">Released</Label>
          <Input
            id="compose-released"
            type="date"
            className="mt-1.5"
            value={releasedOn}
            onChange={(event) => setReleasedOn(event.target.value)}
          />
        </div>
      </div>
      <div>
        <Label htmlFor="compose-notes">Notes</Label>
        <TextArea
          id="compose-notes"
          className="mt-1.5 font-mono text-sm"
          rows={8}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Changelog text"
        />
      </div>
      <div>
        <Label htmlFor="compose-comments">Internal comments</Label>
        <TextArea
          id="compose-comments"
          className="mt-1.5"
          rows={2}
          value={comments}
          onChange={(event) => setComments(event.target.value)}
        />
      </div>
    </section>
  ) : null;

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true" aria-labelledby="firmware-compose-title">
      <form
        className={cn(
          "my-4 flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl",
          mode === "both" ? "max-w-5xl" : "max-w-2xl",
        )}
        onSubmit={handleSubmit}
      >
        <div className="shrink-0 border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="firmware-compose-title" className="text-lg font-semibold text-slate-900">
              {composeTitle(mode)}
            </h2>
            <HoverHintWithGlyph hoverHint={MODE_HELP[mode]} size="md" />
          </div>
        </div>

        <div
          className={cn(
            "min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5",
            mode === "both" ? "grid gap-6 lg:grid-cols-2 lg:items-start" : "space-y-6",
          )}
        >
          {firmwareSection}
          {notesSection}
          {errorMessage ? <p className={cn("text-sm text-rose-700", mode === "both" && "lg:col-span-2")}>{errorMessage}</p> : null}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
